import { useModalLifecycle } from '../shared/useModalLifecycle'
/**
 * Team Spain v0 — Modal de Perfil Deportivo de Atleta (M6)
 * 
 * Permite completar al incorporarse o actualizar datos deportivos:
 * - Nombre, apellidos, fecha de nacimiento, modalidad (Flag/Tackle/Ambos).
 * - Año de inicio, club Flag, club Tackle, ciudad, país.
 * - Posición principal y posiciones secundarias.
 * - Validación de completitud para resolver la obligación inicial de incorporación.
 */

import React, { useState } from 'react'
import { X, UserCheck, AlertCircle, Save } from 'lucide-react'
import { formsService } from './formsService'
import { AthleteProfileData, ModalityType } from '../../types/health'

interface AthleteProfileModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  profile: AthleteProfileData
}

const POSITIONS = ['QB', 'C', 'WR', 'SLOT', 'RUSH', 'LB', 'CB']

export const AthleteProfileModal: React.FC<AthleteProfileModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  profile,
}) => {
  const [firstName, setFirstName] = useState<string>(profile.first_name || '')
  const [lastName, setLastName] = useState<string>(profile.last_name || '')
  const [birthDate, setBirthDate] = useState<string>(profile.birth_date || '')
  const [modality, setModality] = useState<ModalityType>(profile.modality || 'flag')
  const [startYear, setStartYear] = useState<number | ''>(profile.start_year || '')
  const [flagClub, setFlagClub] = useState<string>(profile.flag_club || '')
  const [tackleClub, setTackleClub] = useState<string>(profile.tackle_club || '')
  const [city, setCity] = useState<string>(profile.city || '')
  const [country, setCountry] = useState<string>(profile.country || 'España')
  const [primaryPosition, setPrimaryPosition] = useState<string>(profile.primary_position || 'QB')
  const [secondaryPositions, setSecondaryPositions] = useState<string[]>(
    profile.secondary_positions || []
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const isDirty =
    firstName.trim() !== (profile.first_name || '') ||
    lastName.trim() !== (profile.last_name || '') ||
    birthDate !== (profile.birth_date || '') ||
    modality !== (profile.modality || 'flag') ||
    startYear !== (profile.start_year || '') ||
    flagClub.trim() !== (profile.flag_club || '') ||
    tackleClub.trim() !== (profile.tackle_club || '') ||
    city.trim() !== (profile.city || '') ||
    country.trim() !== (profile.country || 'España') ||
    primaryPosition !== (profile.primary_position || 'QB')

  const handleCloseAttempt = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        'Tienes modificaciones sin guardar en el perfil deportivo. ¿Deseas descartarlas y salir?'
      )
      if (!confirmLeave) return
    }
    onClose()
  }

  useModalLifecycle(isOpen, handleCloseAttempt)

  if (!isOpen) return null

  const handleToggleSecondaryPosition = (pos: string) => {
    if (pos === primaryPosition) return
    if (secondaryPositions.includes(pos)) {
      setSecondaryPositions(secondaryPositions.filter((p) => p !== pos))
    } else {
      setSecondaryPositions([...secondaryPositions, pos])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg('Nombre y apellidos son campos obligatorios.')
      return
    }
    if (!birthDate) {
      setErrorMsg('La fecha de nacimiento es obligatoria.')
      return
    }
    if (!primaryPosition) {
      setErrorMsg('Debes seleccionar al menos una posición principal.')
      return
    }

    setIsSubmitting(true)
    try {
      await formsService.updateAthleteProfile(profile.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birth_date: birthDate,
        modality,
        start_year: startYear === '' ? null : Number(startYear),
        flag_club: flagClub.trim() || null,
        tackle_club: tackleClub.trim() || null,
        city: city.trim() || null,
        country: country.trim() || 'España',
        primary_position: primaryPosition,
        secondary_positions: secondaryPositions.filter((p) => p !== primaryPosition),
      })

      onSuccess()
      onClose()
    } catch (err: any) {
      setErrorMsg(err.message || 'Error al actualizar el perfil deportivo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="mobile-viewport-overlay health-form-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseAttempt()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        padding: '1rem',
      }}
    >
      <div
        className="mobile-viewport-sheet"
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--surface-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                color: 'var(--brand-blue, #3b82f6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UserCheck size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                Perfil deportivo del jugador
              </h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Ficha inicial y actualización de datos deportivos
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseAttempt}
            aria-label="Cerrar modal"
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: '0.65rem 0.75rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              borderRadius: '6px',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <AlertCircle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Nombre y Apellidos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Nombre *
              </label>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Apellidos *
              </label>
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Fecha de Nacimiento y Modalidad */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Fecha de nacimiento *
              </label>
              <input
                type="date"
                required
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Modalidad deportiva *
              </label>
              <select
                value={modality}
                onChange={(e) => setModality(e.target.value as ModalityType)}
                className="input"
                style={{ width: '100%' }}
              >
                <option value="flag">Flag Football 5v5</option>
                <option value="both">Ambos (Flag + Tackle)</option>
                <option value="tackle">Tackle Football</option>
              </select>
            </div>
          </div>

          {/* Posición principal y secundarias */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              Posición principal en campo *
            </label>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {POSITIONS.map((pos) => (
                <button
                  key={pos}
                  type="button"
                  onClick={() => {
                    setPrimaryPosition(pos)
                    setSecondaryPositions(secondaryPositions.filter((p) => p !== pos))
                  }}
                  className={`btn ${primaryPosition === pos ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              Posiciones secundarias (opcional)
            </label>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {POSITIONS.filter((p) => p !== primaryPosition).map((pos) => {
                const isSec = secondaryPositions.includes(pos)
                return (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => handleToggleSecondaryPosition(pos)}
                    style={{
                      padding: '0.3rem 0.6rem',
                      fontSize: '0.75rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: isSec ? '1px solid var(--brand-blue, #3b82f6)' : '1px solid var(--surface-border)',
                      backgroundColor: isSec ? 'rgba(59, 130, 246, 0.12)' : 'var(--surface-sunken)',
                      color: isSec ? 'var(--brand-blue, #3b82f6)' : 'var(--text-secondary)',
                      fontWeight: isSec ? 700 : 500,
                    }}
                  >
                    {pos}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Clubes y Año de inicio */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Club de Flag
              </label>
              <input
                type="text"
                placeholder="Ej. Las Rozas Black Demons"
                value={flagClub}
                onChange={(e) => setFlagClub(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Club de Tackle (si aplica)
              </label>
              <input
                type="text"
                placeholder="Ej. Black Demons Tackle"
                value={tackleClub}
                onChange={(e) => setTackleClub(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Año de inicio en el deporte
              </label>
              <input
                type="number"
                min="1990"
                max={new Date().getFullYear()}
                placeholder="Ej. 2020"
                value={startYear}
                onChange={(e) => setStartYear(e.target.value === '' ? '' : Number(e.target.value))}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Ciudad de residencia
              </label>
              <input
                type="text"
                placeholder="Ej. Madrid, Valencia..."
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                País
              </label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Botones de acción */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              <Save size={15} style={{ marginRight: '0.3rem' }} />
              {isSubmitting ? 'Guardando...' : 'Guardar perfil deportivo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
